import { createFileRoute, Link } from "@tanstack/react-router";
import { cta } from "@/lib/analytics";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { RelatedLinks } from "@/components/related-links";
import { GroupCallCta } from "@/components/booking-cta";
import { GROUP_HOURS_LABEL, GROUP_PRICE_FALLBACK, TODO, groupNotFor, groupProgram, groupSafety, groupStatus } from "@/content/booking";
import { FaqDetails } from "@/components/faq-details";
import portrait from "@/assets/sylwia-portrait.jpg.asset.json";
import ogSocial from "@/assets/og-sylwia-sitkowska.jpg";
import { abs, serviceSchema, breadcrumbSchema, personSchema, webPageSchema } from "@/content/seo";

const title = "Mastermind dla przedsiębiorców i liderów | Sylwia Sitkowska";
const description =
  "Mastermind dla liderów, właścicieli firm i menedżerów. Grupa do 10 osób, 9 miesięcy pracy online i w Warszawie. Prowadzi psycholog dla liderów. Umów rozmowę.";
const ogImage = abs(ogSocial);

const faq = [
  {
    q: "Czym jest grupa mastermind dla przedsiębiorców?",
    a: "To zamknięta grupa rozwojowa dla osób prowadzących firmy i zarządzających zespołami. Spotykacie się regularnie, żeby pracować nad realnymi sytuacjami z organizacji: decyzjami, relacjami ze wspólnikami i zespołem, presją i granicami. Grupę prowadzi psycholog i psychoterapeutka z własnym doświadczeniem w prowadzeniu firm.",
  },
  {
    q: "Czym ten mastermind różni się od mastermindów biznesowych?",
    a: "Większość mastermindów koncentruje się na celach biznesowych i wymianie taktyk. Tutaj w centrum jest osoba, która zarządza: sposób podejmowania decyzji, reakcje pod presją, granice i odpowiedzialność. Realne sytuacje z organizacji są punktem wyjścia, a nie tematem samym w sobie.",
  },
  {
    q: "Kiedy startuje kolejna edycja?",
    a: "Start edycji ustalam dopiero po zakończeniu rozmów kwalifikacyjnych i skompletowaniu pełnej grupy. Dzięki temu mogę zadbać o właściwy skład uczestników i bezpieczeństwo procesu. Jeśli chcesz wziąć udział, najlepiej zgłoś się już teraz, rozmowa kwalifikacyjna jest bezpłatna i nie zobowiązuje.",
  },
  {
    q: "Czy mastermind dla liderów jest terapią grupową?",
    a: "Nie. To proces rozwojowy dla osób zarządzających, prowadzony przez psychologa i psychoterapeutkę. Punktem wyjścia są realne sytuacje z Twojej roli. Jeśli podczas rozmowy wstępnej okaże się, że lepszą formą będzie dla Ciebie terapia, powiem Ci o tym wprost.",
  },
  {
    q: "Ile osób liczy grupa?",
    a: "Maksymalnie 10 osób. Skład jest stały przez cały cykl.",
  },
  {
    q: "Jak długo trwa cykl?",
    a: `Dziewięć miesięcy, dziewięć spotkań, ${GROUP_HOURS_LABEL}: dwa spotkania całodniowe stacjonarnie w Warszawie, pl. Konstytucji 6/74 i siedem spotkań online trwających od 3 do 4 godzin.`,
  },
  {
    q: "Co obejmuje cena?",
    a: `Cena 18 000 zł obejmuje udział w całym, 9-miesięcznym cyklu mastermindu: 9 spotkań i ${GROUP_HOURS_LABEL}, 2 całodniowe spotkania stacjonarne w Warszawie, pl. Konstytucji 6/74, 7 spotkań online trwających od 3 do 4 godzin, pracę w stałej, zamkniętej grupie liczącej maksymalnie 10 osób, przygotowanie i prowadzenie całego programu przez Sylwię Sitkowską. Cena dotyczy całego programu. Płatność następuje po rozmowie kwalifikacyjnej i potwierdzeniu udziału w grupie.`,
  },
  {
    q: "Czy spotkania są poufne?",
    a: "Tak. Obowiązuje pełna poufność: to, co dzieje się w grupie, zostaje w grupie. Zasady omawiamy na starcie cyklu.",
  },
  {
    q: "Jak wygląda rozmowa wstępna?",
    a: "To 30-minutowa rozmowa o Twojej sytuacji, skali odpowiedzialności i oczekiwaniach. Sprawdzamy wzajemne dopasowanie i odpowiadam na pytania organizacyjne.",
  },
  {
    q: "Czy rozmowa zobowiązuje do udziału?",
    a: "Nie. Rozmowa jest poufna i nie zobowiązuje żadnej ze stron. Decyzję podejmujemy wspólnie po jej zakończeniu.",
  },
  {
    q: "Czy można dołączyć w trakcie cyklu?",
    a: "Nie. Grupa pracuje w stałym, zamkniętym składzie, dlatego dołącza się przed startem edycji.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faq.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: { "@type": "Answer", text: item.a },
  })),
};


const serviceJsonLd = serviceSchema({
  name: "Mastermind dla liderów",
  description,
  path: "/mastermind",
  price: "18000",
  audience: "Właściciele firm i liderzy zarządzający zespołami",
  serviceType: "Zamknięta grupa rozwojowa dla liderów",
});

const breadcrumbJsonLd = breadcrumbSchema([
  { name: "Strona główna", path: "/" },
  { name: "Mastermind dla liderów", path: "/mastermind" },
]);

const pageJsonLd = webPageSchema({
  title,
  description,
  path: "/mastermind",
  aboutName: "Mastermind dla liderów",
  speakableSelectors: ["h1"],
});

export const Route = createFileRoute("/mastermind")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      {
        name: "keywords",
        content:
          "mastermind dla liderów, mastermind dla właścicieli firm, mastermind dla przedsiębiorców, grupa mastermind, psychologia biznesu, psychologia biznesu Warszawa, coaching menedżerski, grupa rozwojowa dla liderów, mastermind Warszawa, mastermind online",
      },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://sylwiasitkowska.pl/mastermind" },
      { property: "og:image", content: ogImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: ogImage },
    ],
    links: [{ rel: "canonical", href: "https://sylwiasitkowska.pl/mastermind" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(serviceJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(pageJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(personSchema) },
    ],
  }),
  component: MMCirclePage,
});

const dlaKogo = [
  "Prowadzisz firmę lub organizację od co najmniej 5 lat.",
  "Zarządzasz zespołem co najmniej 10 osób, bezpośrednio lub przez menedżerów.",
  "Chcesz pracować w gronie osób o podobnej skali odpowiedzialności.",
  "Potrzebujesz poufnej przestrzeni do rozmowy o realnych sytuacjach.",
  "Jesteś gotowa lub gotowy nie tylko opowiadać o problemach, ale również pracować nad własnym sposobem funkcjonowania.",
];

const czymJest = [
  "Zamkniętą, stałą grupą.",
  "Poufną przestrzenią do omawiania realnych sytuacji.",
  "Pracą nad decyzjami, reakcjami, relacjami, granicami i odpowiedzialnością.",
  "Procesem prowadzonym przez psychologa, psychoterapeutkę i przedsiębiorczynię.",
];

const czymNieJest = [
  "Networkingiem.",
  "Klubem biznesowym.",
  "Szkoleniem z prowadzenia firmy.",
  "Programem nastawionym wyłącznie na wyniki finansowe.",
  "Terapią grupową.",
];

const jakPracujemy = [
  "Uczestnik wnosi realną sytuację.",
  "Grupa pomaga zobaczyć ją z różnych perspektyw.",
  "Prowadząca dba o strukturę, bezpieczeństwo i pogłębienie tematu.",
  "Praca wraca do sposobu funkcjonowania osoby odpowiedzialnej za decyzje.",
  "Celem nie jest dawanie szybkich rad ani ocenianie uczestnika.",
];

const coMozeZmienic = [
  "Decyzje podejmowane pod presją i przy niepełnych informacjach.",
  "Własne reakcje, schematy i zachowania, które powtarzają się w trudnych sytuacjach.",
  "Granice w relacjach ze wspólnikami, zespołem, klientami i bliskimi.",
  "Odróżnianie realnej odpowiedzialności od potrzeby kontrolowania wszystkiego.",
  "Trudność w delegowaniu i oddawaniu odpowiedzialności innym.",
  "Konflikty i relacje, które zaczynają wpływać na sposób zarządzania.",
  "Samotność, która często pojawia się wraz z odpowiedzialnością za innych.",
  "Miejsce na życie poza pracą bez rezygnowania z ambicji.",
];

const kroki = [
  "Wypełniasz formularz zgłoszeniowy.",
  "Umawiamy bezpłatną rozmowę trwającą 30 minut.",
  "Sprawdzamy, czy program odpowiada Twojej sytuacji i czy profil grupy jest właściwy.",
  "Po wspólnej decyzji otrzymujesz umowę i informacje dotyczące płatności.",
  "Dołączasz do zamkniętej edycji programu.",
];

function SectionMark({ number, label }: { number?: string; label: string }) {
  return (
    <div className="flex items-baseline gap-4">
      {number && <span className="font-display text-sm text-ink-soft">{number}</span>}
      <p className="eyebrow">{label}</p>
    </div>
  );
}

function Bullets({ items }: { items: string[] }) {
  return (
    <ul className="mt-6 space-y-4">
      {items.map((item) => (
        <li key={item} className="flex gap-4 text-base leading-relaxed text-ink/80">
          <span aria-hidden="true" className="mt-2 size-1.5 shrink-0 bg-coral" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function MMCirclePage() {
  const withFallback = (items: { label: string; value: string | null }[]) =>
    items.map((item) => ({
      label: item.label,
      value: item.value ?? (item.label === "Cena" ? GROUP_PRICE_FALLBACK : TODO),
      pending: !item.value,
    }));

  // Cenę i jej zakres pokazuje dedykowana sekcja „Cena" — tutaj pomijamy te wiersze,
  // żeby nie powielać informacji.
  const program = withFallback(groupProgram).filter(
    (item) => item.label !== "Cena" && item.label !== "Co obejmuje cena",
  );
  const safety = withFallback(groupSafety);
  const status = withFallback(groupStatus);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="tresc-glowna">
        {/* Hero */}
        <section className="border-b border-steel">
          <div className="container-page section-pad">
            <nav aria-label="Okruszki" className="mb-8 text-sm text-ink-soft">
              <ol className="flex flex-wrap items-center gap-2">
                <li>
                  <Link to="/" className="underline underline-offset-4 hover:text-coral-text">
                    Strona główna
                  </Link>
                </li>
                <li aria-hidden="true">/</li>
                <li aria-current="page" className="text-ink">
                  Mastermind dla liderów
                </li>
              </ol>
            </nav>
            <SectionMark label="Mastermind dla liderów" />
            <h1 className="serif-display mt-8 max-w-3xl text-[2.4rem] md:text-[3.6rem]">
              Kameralna grupa dla ludzi, od których wiele zależy
            </h1>
            <div className="mt-10 h-px w-full bg-steel" />
            <p className="measure mt-8 text-base leading-relaxed text-ink/80 md:text-lg">
              Stała, zamknięta grupa właścicieli firm, członków zarządów i liderów. Ludzi, którzy
              na co dzień odpowiadają za innych i chcą mieć miejsce do rozmowy z osobami
              mierzącymi się z podobną skalą decyzji, presji i odpowiedzialności.
            </p>
            <p className="measure mt-4 text-base leading-relaxed text-ink/80 md:text-lg">
              To mastermind dla liderów, właścicieli firm i przedsiębiorców, którzy szukają
              kameralnej grupy rozwojowej do pracy nad decyzjami, presją, relacjami i własnym
              sposobem zarządzania.
            </p>
            <ul className="stagger mt-8 flex flex-wrap gap-x-8 gap-y-3">
              {status.map((item) => (
                <li key={item.label} className="text-base leading-relaxed">
                  <span className="text-xs uppercase tracking-[0.16em] text-ink-soft">
                    {item.label}:
                  </span>{" "}
                  <span className={item.pending ? "text-coral-text" : "text-ink"}>{item.value}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap gap-4">
              <GroupCallCta cta={cta("mastermind-rozmowa-wstepna", "hero", "mastermind")}>Umów bezpłatną rozmowę (30 min)</GroupCallCta>
            </div>
            <p className="measure mt-4 text-base leading-relaxed text-ink-soft">
              Rozmowa służy sprawdzeniu wzajemnego dopasowania i nie zobowiązuje do dołączenia do
              grupy.
            </p>
          </div>
        </section>

        {/* Dla kogo */}
        <section className="border-b border-steel bg-surface">
          <div className="container-page grid gap-10 py-12 md:grid-cols-[0.4fr_1fr] md:gap-16 md:py-16">
            <div>
              <SectionMark number="01" label="Dla kogo" />
              <h2 className="display-xl mt-8 text-[1.9rem] md:text-[2.4rem]">
                Kryteria dołączenia do grupy
              </h2>
            </div>
            <div className="max-w-2xl">
              <div className="h-px w-full bg-steel" />
              <Bullets items={dlaKogo} />
              {groupNotFor.length > 0 && (
                <div className="mt-10 border-t border-steel pt-8">
                  <p className="eyebrow">Ta grupa nie będzie właściwa, jeśli…</p>
                  <Bullets items={groupNotFor} />
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Czym jest / czym nie jest */}
        <section className="border-b border-steel">
          <div className="container-page section-pad">
            <SectionMark number="02" label="Czym jest, a czym nie jest mastermind" />
            <div className="mt-10 grid gap-px border border-steel bg-steel md:grid-cols-2">
              <div className="bg-background p-8">
                <h3 className="display-xl text-[1.3rem] md:text-[1.5rem]">Czym jest</h3>
                <Bullets items={czymJest} />
              </div>
              <div className="bg-background p-8">
                <h3 className="display-xl text-[1.3rem] md:text-[1.5rem]">Czym nie jest</h3>
                <Bullets items={czymNieJest} />
              </div>
            </div>
          </div>
        </section>

        {/* Program */}
        <section id="program" className="border-b border-steel bg-surface scroll-mt-24">
          <div className="container-page section-pad">
            <SectionMark number="03" label="Program i organizacja" />
            <h2 className="display-xl mt-8 text-[1.9rem] md:text-[2.4rem]">Co obejmuje cykl</h2>
            <dl className="mt-10 border-t border-steel">
              {program.map((item) => (
                <div
                  key={item.label}
                  className="grid gap-2 border-b border-steel py-5 md:grid-cols-[0.35fr_1fr]"
                >
                  <dt className="text-xs uppercase tracking-[0.16em] text-ink-soft">
                    {item.label}
                  </dt>
                  <dd
                    className={`text-base leading-relaxed ${item.pending ? "text-coral-text" : "text-ink/80"}`}
                  >
                    {item.value}
                  </dd>
                </div>
              ))}
              <div className="grid gap-2 border-b border-steel py-5 md:grid-cols-[0.35fr_1fr]">
                <dt className="text-xs uppercase tracking-[0.16em] text-ink-soft">
                  Start edycji
                </dt>
                <dd className="text-base leading-relaxed text-ink/80">
                  Ustalam indywidualnie, gdy grupa zostanie skompletowana po bezpłatnych, 30-minutowych rozmowach kwalifikacyjnych.
                </dd>
              </div>
            </dl>
          </div>
        </section>

        {/* Poufność i bezpieczeństwo grupy */}
        <section className="border-b border-steel">
          <div className="container-page section-pad">
            <SectionMark number="04" label="Poufność i bezpieczeństwo grupy" />
            <h2 className="display-xl mt-8 text-[1.9rem] md:text-[2.4rem]">
              Zasady, które chronią grupę
            </h2>
            <dl className="mt-10 border-t border-steel">
              {safety.map((item) => (
                <div
                  key={item.label}
                  className="grid gap-2 border-b border-steel py-5 md:grid-cols-[0.35fr_1fr]"
                >
                  <dt className="text-xs uppercase tracking-[0.16em] text-ink-soft">
                    {item.label}
                  </dt>
                  <dd
                    className={`text-base leading-relaxed ${item.pending ? "text-coral-text" : "text-ink/80"}`}
                  >
                    {item.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* Jak pracujemy */}
        <section className="border-b border-steel">
          <div className="container-page grid gap-10 py-12 md:grid-cols-[0.4fr_1fr] md:gap-16 md:py-16">
            <div>
              <SectionMark number="05" label="Jak pracujemy" />
            </div>
            <div className="max-w-2xl">
              <ol className="border-t border-steel">
                {jakPracujemy.map((krok, i) => (
                  <li
                    key={krok}
                    className="grid gap-2 border-b border-steel py-5 md:grid-cols-[3rem_1fr]"
                  >
                    <span className="font-display text-sm text-ink-soft">{`0${i + 1}`}</span>
                    <span className="text-base leading-relaxed text-ink/80">{krok}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        {/* Co może zmienić */}
        <section className="border-b border-steel bg-surface">
          <div className="container-page section-pad">
            <SectionMark number="06" label="Nad czym pracujemy" />
            <div className="mt-8 grid gap-px border border-steel bg-steel sm:grid-cols-2 lg:grid-cols-3">
              {coMozeZmienic.map((item) => (
                <p key={item} className="bg-surface p-6 text-base leading-relaxed text-ink/80">
                  {item}
                </p>
              ))}
            </div>
          </div>
        </section>

        {/* Prowadząca */}
        <section className="border-b border-steel">
          <div className="container-page grid gap-10 py-12 md:grid-cols-[0.4fr_1fr] md:gap-16 md:py-16">
            <div>
              <SectionMark number="07" label="Prowadząca i wiarygodność" />
            </div>
            <div className="max-w-2xl">
              <h2 className="display-xl text-[1.6rem] md:text-[2rem]">
                Prowadząca: Sylwia Sitkowska
              </h2>
              <p className="mt-6 text-base leading-relaxed text-ink/80 md:text-lg">
                Jestem psychologiem, psychoterapeutką CBT i przedsiębiorczynią. Mam za sobą
                10 000 h pracy 1:1. Jednocześnie od 2009 roku rozwijam firmy. Dziś
                organizacja, którą współtworzę, obejmuje 30 placówek i około 300 osób.
              </p>
              <Link
                to="/o-mnie"
                className="mt-6 inline-flex min-h-11 items-center text-base font-medium text-ink underline underline-offset-4 transition-colors hover:text-coral-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral"
              >
                Poznaj moje doświadczenie i kwalifikacje
              </Link>

            </div>
          </div>
        </section>

        {/* Cena */}
        <section className="border-b border-steel bg-surface">
          <div className="container-page section-pad">
            <SectionMark number="08" label="Cena" />
            <div className="mt-10 grid gap-10 border border-steel bg-background p-8 md:grid-cols-[0.35fr_1fr] md:p-12">
              <div>
                <p className="display-xl text-[2.2rem] md:text-[2.8rem]">18 000 zł</p>
                <p className="mt-2 text-base text-ink-soft">za cały cykl</p>
                <p className="mt-2 text-base leading-relaxed text-ink/80">
                  Cena do zapłaty (brutto, usługa zwolniona z VAT)
                </p>

              </div>
              <div>
                <p className="eyebrow">Co obejmuje cena?</p>
                <p className="mt-6 text-base leading-relaxed text-ink/80">
                  Cena 18 000 zł obejmuje udział w całym, 9-miesięcznym cyklu mastermindu:
                </p>
                <Bullets
                  items={[
                    `9 spotkań i ${GROUP_HOURS_LABEL}.`,
                    "2 całodniowe spotkania stacjonarne w Warszawie, pl. Konstytucji 6/74.",
                    "7 spotkań online trwających od 3 do 4 godzin.",
                    "Pracę w stałej, zamkniętej grupie liczącej maksymalnie 10 osób.",
                    "Przygotowanie i prowadzenie całego programu przez Sylwię Sitkowską.",
                    "Cena dotyczy całego programu. Płatność następuje po rozmowie kwalifikacyjnej i potwierdzeniu udziału w grupie.",
                  ]}
                />
                <div className="mt-8">
                  <GroupCallCta cta={cta("mastermind-rozmowa-wstepna", "program", "mastermind")}>Umów bezpłatną rozmowę (30 min)</GroupCallCta>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Proces dołączenia */}
        <section className="border-b border-steel">
          <div className="container-page grid gap-10 py-12 md:grid-cols-[0.4fr_1fr] md:gap-16 md:py-16">
            <div>
            <SectionMark number="09" label="Jak wygląda dołączenie do grupy" />
            </div>
            <div className="max-w-2xl">
              <ol className="border-t border-steel">
                {kroki.map((krok, i) => (
                  <li
                    key={krok}
                    className="grid gap-2 border-b border-steel py-5 md:grid-cols-[3rem_1fr]"
                  >
                    <span className="font-display text-sm text-ink-soft">{`0${i + 1}`}</span>
                    <span className="text-base leading-relaxed text-ink/80">{krok}</span>
                  </li>
                ))}
              </ol>
              <p className="mt-8 border-l-2 border-coral pl-6 text-base leading-relaxed text-ink">
                Rozmowa nie zobowiązuje do udziału i nie oznacza automatycznego przyjęcia do grupy.
                Proces kwalifikacji pomaga zadbać o bezpieczeństwo i właściwy skład grupy.
              </p>
              <p className="mt-8 text-base leading-relaxed text-ink/80">
                W zgłoszeniu wystarczy: imię i nazwisko, rola, wielkość zespołu lub organizacji, jak
                długo prowadzisz firmę albo pełnisz rolę zarządczą, czego teraz potrzebujesz oraz
                adres e-mail.
              </p>
              <p className="mt-4 text-base leading-relaxed text-ink-soft">
                Zgłoszenie traktuję poufnie. Dane wykorzystuję wyłącznie do kontaktu w sprawie
                rozmowy wstępnej i udziału w grupie.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <GroupCallCta cta={cta("mastermind-rozmowa-wstepna", "zapisy", "mastermind")}>Umów bezpłatną rozmowę (30 min)</GroupCallCta>
                <Link
                  to="/kontakt"
                  {...cta("mastermind-formularz", "zapisy", "mastermind")}
                  className="btn-base btn-outline inline-flex min-h-11 items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral"
                >
                  Wypełnij formularz zgłoszeniowy
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section aria-labelledby="faq-group" className="border-b border-steel bg-surface">
          <div className="container-page section-pad">
            <SectionMark number="10" label="Najczęstsze pytania" />
            <h2 id="faq-group" className="display-xl mt-8 text-[1.9rem] md:text-[2.4rem]">
              Zanim się zgłosisz
            </h2>
            <FaqDetails items={faq} className="mt-10" />
          </div>
        </section>

        {/* Końcowe CTA */}
        <section className="grain bg-ink">
          <div className="container-page py-12 text-mist md:py-16">
            <p className="eyebrow-light">Kolejny krok</p>
            <h2 className="display-xl mt-8 max-w-3xl text-[1.9rem] md:text-[2.6rem]">
              Sprawdźmy, czy ta grupa jest właściwym miejscem dla Ciebie.
            </h2>
            <div className="mt-10">
              <GroupCallCta cta={cta("mastermind-rozmowa-wstepna", "domkniecie", "mastermind")} className="btn-base border border-mist text-mist hover:bg-mist hover:text-ink">
                Umów bezpłatną rozmowę (30 min)
              </GroupCallCta>
              <p className="mt-4 text-base leading-relaxed text-mist/80">
                Rozmowa jest bezpłatna i nie zobowiązuje do udziału.
              </p>
            </div>
          </div>
        </section>
      </main>
      <RelatedLinks
        exclude={["/mastermind"]}
        only={["/mastermind-dla-przedsiebiorcow", "/samotnosc-lidera", "/wypalenie-zawodowe-u-lidera", "/konsultacje"]}
        heading={"Przeczytaj i wybierz dalszy krok"}
      />
      <SiteFooter />
    </div>
  );
}
